import { JaaSMeeting, JitsiMeeting } from '@jitsi/react-sdk';
import React, { useEffect, useRef, useState } from 'react';
import CustomWaitingRoom from './CustomWaitingRoom';
import PermissionErrorModal from './PermissionErrorModal';

const JitsiMeetingView = ({ userName = 'Participant', roomName = 'meeting-room-123', onMeetingEnd, meetingType = 'webinar' }) => {
    const apiRef = useRef();
    const [ logItems, updateLog ] = useState([]);
    const [ showNew, toggleShowNew ] = useState(false);
    const [ knockingParticipants, updateKnockingParticipants ] = useState([]);

    // isConnected: has the local participant successfully joined the Jitsi conference
    //              (i.e. `videoConferenceJoined` has fired at least once, OR we've fallen back)?
    // hostPresent: is a moderator/host in the room right now?
    //              Defaults to `true` (optimistic) so the "Waiting for host" screen
    //              is NEVER shown until we've explicitly confirmed the host is absent.
    //              This is what removes the flash of the purple screen for the common
    //              case where the host has already joined.
    const [ isConnected, setIsConnected ] = useState(false);
    const [ hostPresent, setHostPresent ] = useState(true);

    // showPermissionModal: set true when the Jitsi iframe posts
    // `JITSI_MIC_PERMISSION_DENIED` (see the tracks middleware in
    // `jitsi-meet-webinar-lib/react/features/base/tracks/middleware.web.ts`).
    // That signal fires only when ALL three conditions hold:
    //   1. Moderator has enabled the mic (else the toolbar button is
    //      disabled and no click / gUM occurs)
    //   2. User actively taps the mic
    //   3. OS mic permission is not granted (gUM fails → TRACK_CREATE_ERROR)
    // No manual condition tracking is needed on the portal side; the
    // postMessage itself is the "all three true" signal.
    const [ showPermissionModal, setShowPermissionModal ] = useState(false);

    // Cover-first: render the purple screen until we have BOTH
    // (a) confirmed the meeting is joinable (`isConnected === true`) AND
    // (b) confirmed a host is in the room (`hostPresent === true`).
    //
    // The overlay sits on top of the iframe from t=0. Two things prevent
    // this from producing a visible purple flash on the happy path:
    //   1. The RN mint splash covers the WebView completely until
    //      `MEETING_READY` postMessage arrives (see Phoenix
    //      `eventWebinar/index.tsx`, `fallbackMs = 20000` for jitsi). The
    //      long safety timer ensures the splash never gives up before
    //      MEETING_READY on real-device iOS (mic prompt + join can take
    //      10-15s of wall-clock time).
    //   2. `MEETING_READY` is posted the instant `isConnected` becomes true.
    //      On videoConferenceJoined, BOTH `isConnected` and `hostPresent`
    //      flip true in the same synchronous handler → React batches into a
    //      single render → the mint splash hides AND the purple overlay
    //      unmounts in the same paint. User sees mint → meeting UI directly.
    //
    // Auth-required path: `errorOccurred` → schedulePromote (1.2s debounce)
    // → `promoteToWaitingRoom` sets `isConnected=true, hostPresent=false`
    // → MEETING_READY posts (splash hides) AND purple stays rendered → user
    // sees mint → purple.
    //
    // Do NOT change to `!hostPresent` alone: that removes the cover from
    // t=0 and if the mint splash ever hides before videoConferenceJoined
    // fires, the underlying Jitsi iframe (dark background / loading state)
    // is exposed for 2-3s. This is exactly the "black gap" regression that
    // appeared when this line was tried as `!hostPresent`.
    const showWaitingRoom = !isConnected || !hostPresent;

    // Mirror the latest isConnected value into a ref so the fallback timer can
    // read it without re-running the effect.
    const isConnectedRef = useRef(false);
    useEffect(() => { isConnectedRef.current = isConnected; }, [isConnected]);

    // Detect whether a host/moderator is in the room right now.
    // Strategy 1 (preferred): any participant with role === 'moderator'.
    //   Works reliably when the host joins with a JWT that carries a moderator claim
    //   (the admin portal does this).
    // Strategy 2 (fallback): total participant count > 1 (i.e., someone else is here).
    //   Used when role information is not available. Consistent with the webinar
    //   model where the host is expected to be in the room before participants.
    // On error, we return `true` (fail-safe): we prefer to briefly show an empty
    // meeting over incorrectly displaying "Waiting for host" when the host IS there.
    const detectHostPresence = () => {
        const api = apiRef.current;
        if (!api) return false;
        try {
            const participants = typeof api.getParticipantsInfo === 'function'
                ? (api.getParticipantsInfo() || [])
                : [];
            if (Array.isArray(participants) && participants.some(p => p && p.role === 'moderator')) {
                return true;
            }
            const total = typeof api.getNumberOfParticipants === 'function'
                ? api.getNumberOfParticipants()
                : (Array.isArray(participants) ? participants.length : 0);
            return total > 1;
        } catch (e) {
            console.warn('[Portal JitsiMeetingView] detectHostPresence error', e);
            return true;
        }
    };

    useEffect(() => {
        const handleJitsiMessage = (event) => {
            try {
                const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                if (data?.type === 'JITSI_HOST_PRESENT') {
                    console.log('[Portal JitsiMeetingView] JITSI_HOST_PRESENT received, hiding custom lobby');
                    // Preserve backwards-compatible behaviour: any outside signal that
                    // "the host is here" should dismiss the waiting screen immediately.
                    setIsConnected(true);
                    setHostPresent(true);
                } else if (data?.type === 'JITSI_MIC_PERMISSION_DENIED') {
                    // Deterministic signal from the Jitsi iframe's tracks
                    // middleware (see `middleware.web.ts` TRACK_CREATE_ERROR
                    // handler). Fires ONLY after all three conditions are
                    // satisfied simultaneously:
                    //   1. Moderator has enabled the mic (else the toolbar
                    //      button is disabled and no click / gUM occurs)
                    //   2. User actively tapped the mic (`ensureTrack:true`
                    //      is set only for user-initiated unmute)
                    //   3. OS mic permission not granted (else gUM succeeds
                    //      and TRACK_CREATE_ERROR never fires)
                    // The React `showPermissionModal` state suppresses
                    // duplicates — repeat messages while the modal is open
                    // are effectively no-ops.
                    console.log('[Portal JitsiMeetingView] JITSI_MIC_PERMISSION_DENIED received, showing recovery modal');
                    setShowPermissionModal(true);
                } else if (data?.type === 'SETTINGS_RETURNED') {
                    // RN wrapper reports the user just came back from the
                    // OS mic-permission Settings screen. Two-step recovery:
                    //   1. Reset the Jitsi bundle's audioDeniedThisSession
                    //      suppression flag by postMessaging RESET_AUDIO_
                    //      SUPPRESSION into the iframe. Without this, the
                    //      wrapper in Jitsi's app.js short-circuits every
                    //      subsequent audio gUM call with "suppressed after
                    //      first deny" — even after OS granted permission.
                    //      Diagnosed via live test on Motorola Edge 40 neo:
                    //      native MediaDevices.prototype.getUserMedia
                    //      SUCCEEDS post-grant, only the wrapper blocks.
                    //   2. Then call toggleAudio. With the flag reset, the
                    //      wrapper allows the call through to Chromium →
                    //      OS check passes → mic goes live with zero taps.
                    // If OS is still denied (user changed mind), gUM fails
                    // → TRACK_CREATE_ERROR → JITSI_MIC_PERMISSION_DENIED
                    // path fires again → modal reappears. No broken state.
                    console.log('[Portal JitsiMeetingView] SETTINGS_RETURNED received, retrying mic');
                    setShowPermissionModal(false);
                    try {
                        const api = apiRef.current;

                        // Step 1: reset the suppression flag
                        if (api && typeof api.getIFrame === 'function') {
                            try {
                                const iframe = api.getIFrame();
                                if (iframe && iframe.contentWindow) {
                                    iframe.contentWindow.postMessage(
                                        { type: 'RESET_AUDIO_SUPPRESSION' }, '*');
                                }
                            } catch (resetErr) {
                                console.log('[Portal JitsiMeetingView] RESET_AUDIO_SUPPRESSION failed', resetErr);
                            }
                        }

                        // Step 2: give the reset one tick to land inside
                        // the iframe's message queue before triggering
                        // the retry (otherwise a race can execute the
                        // wrapped gUM before the flag flips).
                        if (api && typeof api.isAudioMuted === 'function') {
                            setTimeout(() => {
                                Promise.resolve(api.isAudioMuted())
                                    .then(muted => {
                                        if (muted && typeof api.executeCommand === 'function') {
                                            api.executeCommand('toggleAudio');
                                        }
                                    })
                                    .catch(err => {
                                        console.log('[Portal JitsiMeetingView] SETTINGS_RETURNED isAudioMuted error', err);
                                    });
                            }, 50);
                        }
                    } catch (e) {
                        console.log('[Portal JitsiMeetingView] SETTINGS_RETURNED retry failed', e);
                    }
                }
            } catch (error) {
                console.log('[Portal JitsiMeetingView] ignoring non-JSON message:', event.data, error);
            }
        };
        window.addEventListener('message', handleJitsiMessage);
        return () => window.removeEventListener('message', handleJitsiMessage);
    }, []);

    // Fallback for the "iframe never boots / never joins" scenario: if
    // `videoConferenceJoined` has not fired after 15 s, mark the session as
    // connected AND host-absent so the purple screen appears with its Go
    // Back button.
    //
    // Why 15 s (raised from 8 s):
    //   - Real-device flow includes an OS mic-permission dialog that can
    //     take 3-8 s of user thinking time BEFORE Jitsi even starts joining.
    //     Add Jitsi's own connect/join (~2-5 s) and the worst-case happy
    //     path is ~10-13 s. An 8 s fallback was firing prematurely on iOS
    //     in that window, flipping hostPresent = false and flashing the
    //     purple screen for 2-3 s even when the host was already present.
    //   - Primary auth-required path is handled by the `errorOccurred` /
    //     `passwordRequired` listeners in `handleApiReady` (debounced
    //     1.2 s). Purple appears within ~1.5 s of the failure, so this
    //     fallback is truly a last-resort safety net for the rare case
    //     where the iframe boots but neither joins nor emits a
    //     recognisable error.
    //   - Do NOT restore 8 s here: it re-introduces the purple-flash
    //     regression on real iOS devices with mic permission prompts.
    useEffect(() => {
        const FALLBACK_MS = 15000;
        const timer = setTimeout(() => {
            if (!isConnectedRef.current) {
                console.warn('[Portal JitsiMeetingView] videoConferenceJoined not received after ' + FALLBACK_MS + 'ms — showing waiting room as escape hatch');
                setIsConnected(true);
                setHostPresent(false);
            }
        }, FALLBACK_MS);
        return () => clearTimeout(timer);
    }, []);

    // Coordinated splash: notify the enclosing React Native WebView (if any)
    // the moment the portal has something meaningful to render — either the
    // Jitsi meeting UI (host present) or the "Waiting for host" screen (host
    // absent). The RN wrapper listens for this message and only then hides its
    // own "Connecting you to the live event…" splash, so users never see the
    // black/purple flash that used to occur while Jitsi was still handshaking.
    // Non-RN environments (plain browser) simply have no listener — this is a
    // no-op there.
    const meetingReadyPostedRef = useRef(false);
    useEffect(() => {
        if (!isConnected || meetingReadyPostedRef.current) return;
        try {
            if (typeof window !== 'undefined'
                && window.ReactNativeWebView
                && typeof window.ReactNativeWebView.postMessage === 'function') {
                window.ReactNativeWebView.postMessage('MEETING_READY');
                meetingReadyPostedRef.current = true;
                console.log('[Portal JitsiMeetingView] MEETING_READY posted to RN');
            }
        } catch (e) {
            console.warn('[Portal JitsiMeetingView] MEETING_READY post failed', e);
        }
    }, [isConnected]);

    const printEventOutput = payload => {
        updateLog(items => [ ...items, JSON.stringify(payload) ]);
    };

    const handleAudioStatusChange = (payload, feature) => {
        if (payload.muted) {
            updateLog(items => [ ...items, `${feature} off` ])
        } else {
            updateLog(items => [ ...items, `${feature} on` ])
        }
    };

    const handleChatUpdates = payload => {
     
    };

    const handleKnockingParticipant = payload => {
        updateLog(items => [ ...items, JSON.stringify(payload) ]);
        updateKnockingParticipants(participants => [ ...participants, payload?.participant ])
    };

    const resolveKnockingParticipants = condition => {
        knockingParticipants.forEach(participant => {
            apiRef.current.executeCommand('answerKnockingParticipant', participant?.id, condition(participant));
            updateKnockingParticipants(participants => participants.filter(item => item.id === participant.id));
        });
    };

    const handleJitsiIFrameRef1 = iframeRef => {
        iframeRef.style.border = 'none';
        iframeRef.style.background = '#000';
        iframeRef.style.width = '100vw';
        iframeRef.style.height = '100vh';
        iframeRef.style.position = 'fixed';
        iframeRef.style.top = '0';
        iframeRef.style.left = '0';
        iframeRef.style.zIndex = '9999';
        iframeRef.style.margin = '0';
        iframeRef.style.padding = '0';
        // iframeRef.contentDocument.querySelector('.watermark').style.display = 'none';
    };

    const handleJitsiIFrameRef2 = iframeRef => {
        iframeRef.style.marginTop = '10px';
        iframeRef.style.border = '10px dashed #df486f';
        iframeRef.style.padding = '5px';
        iframeRef.style.height = '400px';
    };

    const handleJaaSIFrameRef = iframeRef => {
        iframeRef.style.border = '10px solid #3d3d3d';
        iframeRef.style.background = '#3d3d3d';
        iframeRef.style.height = '400px';
        iframeRef.style.marginBottom = '20px';
    };

    const handleApiReady = apiObj => {
        apiRef.current = apiObj;

        // Register the join listener FIRST to minimise the race window in which
        // the SDK could emit `videoConferenceJoined` before we're subscribed.
        apiRef.current.on('videoConferenceJoined', () => {
            console.log('[Portal JitsiMeetingView] videoConferenceJoined');
            setIsConnected(true);
            // Successful join implicitly confirms host presence. In this
            // webinar configuration a join can only succeed when a moderator
            // is already in the room; the "no host" path fails at the XMPP
            // auth step with `AUTHENTICATION_REQUIRED`, which we handle
            // separately via the `errorOccurred` / `passwordRequired`
            // listeners below (they set hostPresent = false and show the
            // purple screen). So `videoConferenceJoined` firing at all is
            // proof that a moderator was in the room at join time.
            //
            // We deliberately do NOT call `detectHostPresence()` here.
            // On iOS the SDK's roster (`getParticipantsInfo` /
            // `getNumberOfParticipants`) is stale at `videoConferenceJoined`
            // time — the moderator's presence stanza arrives 500 ms-2 s
            // later via `participantJoined`. A stale-read false was
            // reintroducing the "purple flash on host present" regression
            // (either instantly, or after the 1.5 s delayed re-check).
            //
            // `participantJoined` (moderator role) and
            // `participantRoleChanged` handlers below still set
            // hostPresent = true defensively for the promote-to-moderator
            // flow; they remain unchanged.
            setHostPresent(true);
        });

        // Any remote participant arriving after us may be the host. Prefer the
        // moderator role when the SDK exposes it; otherwise fall back to the
        // count-based heuristic in `detectHostPresence`.
        apiRef.current.on('participantJoined', (participant) => {
            if (participant && participant.role === 'moderator') {
                setHostPresent(true);
                return;
            }
            if (detectHostPresence()) {
                setHostPresent(true);
            }
        });

        // A participant can be promoted to moderator after joining
        // (e.g. via `grantModerator`). Treat this as host present.
        apiRef.current.on('participantRoleChanged', (participant) => {
            if (participant && participant.role === 'moderator') {
                setHostPresent(true);
            }
        });

        // Existing listeners (unchanged) — the duplicate `knockingParticipant`
        // subscription that used to be registered twice has been removed.
        apiRef.current.on('knockingParticipant', handleKnockingParticipant);
        apiRef.current.on('audioMuteStatusChanged', payload => handleAudioStatusChange(payload, 'audio'));
        apiRef.current.on('videoMuteStatusChanged', payload => handleAudioStatusChange(payload, 'video'));
        apiRef.current.on('raiseHandUpdated', printEventOutput);
        apiRef.current.on('titleViewChanged', printEventOutput);
        apiRef.current.on('chatUpdated', handleChatUpdates);

        // Microphone permission recovery is now driven by an explicit
        // postMessage from inside the Jitsi iframe (see the
        // TRACK_CREATE_ERROR handler in `jitsi-meet-webinar-lib`
        // `react/features/base/tracks/middleware.web.ts`). That message
        // is the deterministic signal — no more flip-flop timing games.
        // The `message` event listener that consumes it lives in the
        // top-level useEffect above (search for
        // `JITSI_MIC_PERMISSION_DENIED`).

        // Listen for meeting end events
        apiRef.current.on('videoConferenceLeft', handleMeetingEnd);
        apiRef.current.on('readyToClose', handleMeetingEnd);

        // Auth-required / password-required errors: promote to the purple
        // "Waiting for host" screen immediately instead of waiting for the
        // fallback. Without this a guest joining an owner-less room sees
        // Jitsi's own LoginDialog / WaitForOwnerDialog for the full fallback
        // window because `videoConferenceJoined` never fires (the conference
        // join is blocked at the XMPP auth step). `isConnected = true` +
        // `hostPresent = false` renders CustomWaitingRoom which overlays the
        // iframe (z-index 10000 > 9999) and completely hides the underlying
        // Jitsi dialog.
        //
        // Debounced 1.2 s: if `videoConferenceJoined` fires meanwhile, this
        // is a transient error (network hiccup, retryable XMPP issue) and we
        // must NOT flip `hostPresent = false` — that would re-introduce the
        // purple-flash-on-host-present regression the optimistic
        // `hostPresent = true` default was added to fix. Only a persistent
        // auth error (no join within 1.2 s of the error) actually promotes.
        const promoteToWaitingRoom = () => {
            if (!isConnectedRef.current) {
                console.log('[Portal JitsiMeetingView] promoting to CustomWaitingRoom via early auth error');
                setIsConnected(true);
                setHostPresent(false);
            }
        };

        const schedulePromote = () => {
            if (isConnectedRef.current) return;
            setTimeout(promoteToWaitingRoom, 1200);
        };

        // Match ONLY the specific Jitsi errors that mean "conference cannot
        // be joined without authentication". Broader regex here caused false
        // positives on transient errors and made purple flash for
        // host-present users.
        const isAuthErrorName = name =>
            name === 'connection.passwordRequired'
            || name === 'conference.authenticationRequired'
            || name === 'conference.membersOnly';

        apiRef.current.on('errorOccurred', payload => {
            const errName = payload && (payload.error?.name || payload.name || '');
            if (typeof errName === 'string' && isAuthErrorName(errName)) {
                schedulePromote();
            }
        });
        apiRef.current.on('passwordRequired', schedulePromote);
    };

    const handleMeetingEnd = (event) => {
        console.log('Meeting ended:', event);
        onMeetingEnd(event);
    };
    
    const handleReadyToClose = () => {
        // This is called when Jitsi is ready to close
        handleMeetingEnd({ reason: 'readyToClose' });
    };

    const generateRoomName = () => `JitsiMeetRoomNo${Math.random() * 100}-${Date.now()}`;

    // Multiple instances demo
    const renderNewInstance = () => {
        if (!showNew) {
            return null;
        }

        return (
            <JitsiMeeting
                roomName = { generateRoomName() }
                getIFrameRef = { handleJitsiIFrameRef2 } />
        );
    };

    const renderButtons = () => (
        <div style = {{ margin: '15px 0' }}>
            <div style = {{
                display: 'flex',
                justifyContent: 'center'
            }}>
                <button
                    type = 'text'
                    title = 'Click to execute toggle raise hand command'
                    style = {{
                        border: 0,
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#f8ae1a',
                        color: '#040404',
                        padding: '12px 46px',
                        margin: '2px 2px'
                    }}
                    onClick = { () => apiRef.current.executeCommand('toggleRaiseHand') }>
                    Raise hand
                </button>
                <button
                    type = 'text'
                    title = 'Click to approve/reject knocking participant'
                    style = {{
                        border: 0,
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#0056E0',
                        color: 'white',
                        padding: '12px 46px',
                        margin: '2px 2px'
                    }}
                    onClick = { () => resolveKnockingParticipants(({ name }) => !name.includes('test')) }>
                    Resolve lobby
                </button>
                <button
                    type = 'text'
                    title = 'Click to execute subject command'
                    style = {{
                        border: 0,
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#df486f',
                        color: 'white',
                        padding: '12px 46px',
                        margin: '2px 2px'
                    }}
                    onClick = { () => apiRef.current.executeCommand('subject', 'New Subject')}>
                    Change subject
                </button>
                <button
                    type = 'text'
                    title = 'Click to create a new JitsiMeeting instance'
                    style = {{
                        border: 0,
                        borderRadius: '6px',
                        fontSize: '14px',
                        background: '#3D3D3D',
                        color: 'white',
                        padding: '12px 46px',
                        margin: '2px 2px'
                    }}
                    onClick = { () => toggleShowNew(!showNew) }>
                    Toggle new instance
                </button>
            </div>
        </div>
    );

    const renderLog = () => logItems.map(
        (item, index) => (
            <div
                style = {{
                    fontFamily: 'monospace',
                    padding: '5px'
                }}
                key = { index }>
                {item}
            </div>
        )
    );

    const renderSpinner = () => (
        <div style = {{
            fontFamily: 'sans-serif',
            textAlign: 'center'
        }}>
            Loading..
        </div>
    );


    return (
        <>
            {/* Cover-first overlay: purple screen is rendered until BOTH
                `isConnected` and `hostPresent` are true (see `showWaitingRoom`
                definition above). This means the overlay is present from t=0
                and only unmounts once we've simultaneously confirmed the
                meeting is joinable and a host is in the room.

                Why cover-first: on the auth-required path Jitsi renders its
                own dark LoginDialog / WaitForOwnerDialog inside the iframe
                below (z-index 9999). If we only mounted this overlay after
                detecting the error, there would be a ~1 render-frame gap in
                which the dark Jitsi dialog is visible to the user. Rendering
                the overlay from t=0 (behind the RN mint splash while it's
                still on top) closes that gap entirely.

                Happy path stays flash-free: `videoConferenceJoined` fires,
                its handler sets `isConnected = true` AND `hostPresent = true`
                in the same synchronous block → React batches both updates
                into one re-render → the overlay unmounts and the RN mint
                splash hides in the same paint. */}
            {showWaitingRoom && (
                <CustomWaitingRoom
                    userName={userName}
                    roomName={roomName}
                    onBack={onMeetingEnd}
                />
            )}

            {/* Mic-permission recovery modal (z-index 10001 > waiting room
                10000 > iframe 9999). Bridges to native RN wrapper via
                OPEN_SETTINGS postMessage, which calls Linking.openSettings()
                on the device. Only visible when the Jitsi iframe posts
                `JITSI_MIC_PERMISSION_DENIED` (see handleJitsiMessage). */}
            <PermissionErrorModal
                visible={showPermissionModal}
                onOpenSettings={() => {
                    if (typeof window.ReactNativeWebView?.postMessage === 'function') {
                        window.ReactNativeWebView.postMessage('OPEN_SETTINGS');
                    }
                    setShowPermissionModal(false);
                }}
                onClose={() => setShowPermissionModal(false)}
            />


            {/* Jitsi Meeting - always mounted and visible. When the host is absent
                the CustomWaitingRoom above overlays it (z-index 10000 > iframe 9999). */}
            <div style={{ display: 'block' }}>
                <JitsiMeeting
                domain = {'p-jitsi.phx-app.com' }
                roomName = { roomName }
                spinner = { renderSpinner }
                configOverwrite = {{
                    startWithAudioMuted: true,
                    startWithVideoMuted: true,
                    enableWelcomePage: false,
                    prejoinPageEnabled: false,          // Disable prejoin screen
                    prejoinConfig: {
                        enabled: false                   // Force disable prejoin
                    },
                    disableDeepLinking: true,            // Prevent native app redirects
                    requireDisplayName: false,           // Don't require name input
                    enableInsecureRoomNameWarning: false, // Skip room name warnings
                    disableProfile: false,               // Allow profile but don't force it
                    
                    // Mobile webview specific configurations
                    enableNoAudioDetection: true,       // Disable audio detection warnings
                    enableNoisyMicDetection: true,      // Disable noisy mic detection
                    disableAudioLevels: false,          // Enable audio levels — drives dominant-speaker visual feedback (avatar pulse / thumbnail highlight)
                    
                    // Ensure webview compatibility
                    testing: {
                        mobileXmppWsThreshold: 0         // Force WebSocket for mobile
                    },
                    
                    // Participant/Audience mode - disable moderator capabilities
                    disableRemoteMute: true,              // Cannot mute others
                    disableKick: true,                    // Cannot kick participants
                    disableModeratorIndicator: false,     // Show who is moderator
                    disableReactionsModeration: true,     // Cannot moderate reactions
                    
                    // Disable keyboard shortcuts in settings
                    disableShortcuts: true,               // Disable keyboard shortcuts
                    
                    // Settings - show only audio and video (devices) tab
                    settingSections: ['devices'],         // Only show Audio/Video in settings
                    disableChat: false,                   // Chat re-enabled for participant Q&A
                    disablePrivateChat: true,             // Group chat only — no participant-to-participant DMs

                    // Toolbar configuration - Zoom-like bottom positioning
                    toolbarConfig: {
                        alwaysVisible: false,
                        autoHideWhileChatIsOpen: false,
                        initialTimeout: 4000,
                        timeout: 4000
                    },


                    remoteVideoMenu:{
                      disablePrivateChat: true,
                    },

                    // Toolbar buttons - Conditionally show based on meetingType
                    // For webinar: show settings, hangup, microphone, chat
                    // For other types: hide all toolbar buttons
                    ...(meetingType === 'webinar' && {
                        toolbarButtons: [
                            'settings',
                            'hangup',
                            'microphone',
                            'chat'
                        ]
                    }),
                    
                    // Filmstrip configuration - Completely hide participant video thumbnails
                    filmstrip: {
                        disableResizable: true,
                        disableStageFilmstrip: true,
                        disabled: true
                    },
                    
                    // Disable filmstrip to hide participant videos
                    disableFilmstripAutohiding: true,
                    
                    // Disable self view and remote thumbnails
                    disable1On1Mode: false,
                    
                    // Video quality settings
                    resolution: 720,
                    constraints: {
                        video: {
                            height: {
                                ideal: 720,
                                max: 1080,
                                min: 360
                            }
                        }
                    },
                    
                    // Notifications and sounds - disable all
                    disableJoinLeaveSounds: true,         // Disable join/leave sounds
                    disableSounds: true,                  // Disable all sound effects
                    
                    // Disable all notification sounds
                    disabledSounds: [
                        'ASKED_TO_UNMUTE_SOUND',
                        'E2EE_OFF_SOUND',
                        'E2EE_ON_SOUND',
                        'INCOMING_MSG_SOUND',              // Disable new message sound
                        'KNOCKING_PARTICIPANT_SOUND',
                        'LIVE_STREAMING_OFF_SOUND',
                        'LIVE_STREAMING_ON_SOUND',
                        'NO_AUDIO_SIGNAL_SOUND',
                        'NOISY_AUDIO_INPUT_SOUND',
                        'OUTGOING_CALL_EXPIRED_SOUND',
                        'OUTGOING_CALL_REJECTED_SOUND',
                        'OUTGOING_CALL_RINGING_SOUND',
                        'OUTGOING_CALL_START_SOUND',
                        'PARTICIPANT_JOINED_SOUND',        // Disable joined sound
                        'PARTICIPANT_LEFT_SOUND',          // Disable left sound
                        'RAISE_HAND_SOUND',
                        'REACTION_SOUND',
                        'RECORDING_OFF_SOUND',
                        'RECORDING_ON_SOUND',
                        'TALK_WHILE_MUTED_SOUND'
                    ],
                    
                    // Disable all notifications
                    notifications: [],                     // Empty array = no notifications
                    disableNotifications: true,            // Disable all notification popups
                    
                    // UI optimizations
                    hideConferenceSubject: true,
                    hideConferenceTimer: false,
                    hideParticipantsStats: false,
                    
                    // Disable unnecessary features for cleaner UI
                    disableInviteFunctions: false,
                    doNotStoreRoom: false,
                    
                    // Disable tile view to show only moderator (stage view)
                    startWithTileView: false,
                    disableTileView: true,
                    
                    // Disable self view
                    disableSelfView: true,
                    
                    // Background settings
                    backgroundAlpha: 0.5,
                    
                    // Reactions - completely disable
                    disableReactions: true,               // Disable all emoji reactions
                    disableReactionSounds: true,          // Disable reaction sound effects
                    
                    // Recording - limited for participants
                    fileRecordingsEnabled: false,         // Participants cannot record
                    liveStreamingEnabled: false,          // Participants cannot livestream
                    
                    // Transcription
                    transcribingEnabled: false,
                    
                    
                    // Hide conference info (timer, subject, etc.)
                    conferenceInfo: {
                        alwaysVisible: [],
                        autoHide: []
                    },
                    
                    // Disable connection indicator
                    disableConnectionIndicator: true,
                    
                    // Completely disable virtual backgrounds
                    disableVirtualBackground: true,
                    
                    // Disable raise hand feature
                    disableRaiseHand: true,
                    
                    disableChatSmileys: false,
                    
                    // Disable chat notifications
                    disableChatNotifications: true
                }}
                interfaceConfigOverwrite = {{
                    // Filmstrip settings - Completely hide participant video thumbnails
                    FILM_STRIP_MAX_HEIGHT: 0,
                    VERTICAL_FILMSTRIP: false,
                    
                    // Hide filmstrip completely
                    filmStripOnly: false,
                    DISABLE_RINGING: true,
                    DISABLE_FOCUS_INDICATOR: true,
                    
                    // Video layout - fit entire screen
                    VIDEO_LAYOUT_FIT: 'both',
                    TILE_VIEW_MAX_COLUMNS: 5,
                    
                    // Disable branding for cleaner look
                    SHOW_JITSI_WATERMARK: false,
                    SHOW_WATERMARK_FOR_GUESTS: false,
                    SHOW_BRAND_WATERMARK: false,
                    SHOW_POWERED_BY: false,
                    
                    // Hide Jitsi logo in top left corner
                    DEFAULT_LOGO_URL: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
                    JITSI_WATERMARK_LINK: '',
                    
                    // Hide connection/performance indicator
                    CONNECTION_INDICATOR_DISABLED: true,
                    
                    // Mobile optimizations - Critical for webview
                    MOBILE_APP_PROMO: false,
                    HIDE_DEEP_LINKING_LOGO: true,
                    NATIVE_APP_NAME: '',
                    APP_SCHEME: '',
                    
                    // Notifications - disable all notification UI elements
                    DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,   // Hide participant joined/left notifications
                    DISABLE_PRESENCE_STATUS: true,            // Hide presence status notifications
                    
                    // UI elements
                    DISABLE_DOMINANT_SPEAKER_INDICATOR: false,
                    DISABLE_TRANSCRIPTION_SUBTITLES: false,
                    DISABLE_VIDEO_BACKGROUND: true,              // Hide virtual background option
                    
                    // Hide Shortcuts tab from settings
                    DISABLE_SHORTCUTS: true,
                    
                    // Settings sections available - show only audio and video
                    SETTINGS_SECTIONS: ['devices'],  // 'devices' shows audio and video settings
                    
                    // Display settings
                    DEFAULT_BACKGROUND: '#000000',
                    DEFAULT_REMOTE_DISPLAY_NAME: 'Participant',
                    DEFAULT_LOCAL_DISPLAY_NAME: 'Me',
                    
                    // Optimal browsers
                    OPTIMAL_BROWSERS: ['chrome', 'chromium', 'firefox', 'electron', 'safari'],
                    
                    // Connection indicators
                    CONNECTION_INDICATOR_AUTO_HIDE_ENABLED: true,
                    CONNECTION_INDICATOR_AUTO_HIDE_TIMEOUT: 5000,
                    
                    // Recent list
                    RECENT_LIST_ENABLED: false,
                    
                    // Generate room names
                    GENERATE_ROOMNAMES_ON_WELCOME_PAGE: false,
                    
                    // Hide invite header when alone
                    HIDE_INVITE_MORE_HEADER: false
                }}
                userInfo = {{
                    displayName: userName
                }}
                lang = 'en'
                onApiReady = { externalApi => handleApiReady(externalApi) }
                onReadyToClose = { handleReadyToClose }
                getIFrameRef = { handleJitsiIFrameRef1 } />
            </div>
            
            {/* <JaaSMeeting
                roomName = { generateRoomName() }

                // Update this with the `8x8.vc` or `stage.8x8.vc` version of interest
                // and avoid mixing up different domains and release versions
                // on the same page at the same time, as only the first
                // external api script will be loaded.
                // release = 'release-1234'

                useStaging = { true }
                getIFrameRef = { handleJaaSIFrameRef } /> */}
            {/* {renderButtons()}
            {renderNewInstance()}
            {renderLog()} */}
        </>
    );
};

export default JitsiMeetingView;
